import React, { useState } from "react";
import styled from "styled-components";
import { Field } from "react-final-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumber, CountryCode } from "libphonenumber-js";

interface PhoneNumberProps {
  name?: string;
  error?: string;
  placeholder?: string;
  value?: string;
}

/* -------- Phone Validation -------- */
export const validatePhone = (value: string, country: string) => {
  if (!value) return "Phone number is required";

  try {
    const phone = parsePhoneNumber(value, country.toUpperCase() as CountryCode);

    // Regular validation for all countries
    if (!phone.isValid()) {
      return "Invalid phone number for this country";
    }

    if (!phone.isPossible()) {
      return "Phone number format is not correct";
    }

    // Extra strict check ONLY for Russia
    if (country.toUpperCase() === "RU") {
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, "");

      // Must start with 7 (Russia country code) and have exactly 11 digits
      if (!/^7\d{10}$/.test(digits)) {
        return "Russian phone number must be 10 digits after +7";
      }
    }

  } catch (e) {
    return "Invalid phone number format";
  }

  return undefined;
};





export const PhoneNumber = ({
  name = "phone",
  error,
  placeholder = "555 123-4567",
  value,
  ...rest
}: PhoneNumberProps) => {
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("us");

  return (
    <Field
      name={name}
      validate={(val) => validatePhone(val, country)}  // ✔ pass country to validator
      render={({ input, meta }) => (
        <Container error={!!(error || meta.error)}>
          <Label>Phone Number</Label>

          <PhoneInput
            country={country}
            value={phone}
            placeholder={placeholder}
            onChange={(val: string, countryData: any) => {
              setPhone(val);            // ✔ keep full phone (+37499...)
              input.onChange(val);      // ✔ send full number to form
              setCountry(countryData.countryCode);  // ✔ auto-detect actual country
            }}
            specialLabel=""
            inputProps={{
              name,
              autoComplete: "off",
              placeholder,
            }}
            inputClass="custom-phone-input"
            inputStyle={{
              border: "none",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "16px",
              width: "100%",
            }}
            buttonStyle={{ border: "none", backgroundColor: "transparent" }}
            containerStyle={{ width: "100%" }}
          />

          {meta.error && meta.touched && (
            <Error>{meta.error}</Error>
          )}
        </Container>
      )}
      {...rest}
    />
  );
};

/* ----------- Styled Components ----------- */

const Container = styled.div<{ error?: boolean }>`
  position: relative;
  flex: 1;
  height: ${(props) => (props.error ? 86 : 50)}px !important;
  background-color: #1a1a1a;

  .react-tel-input {
    display: flex;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    background-color: transparent !important;
  }

  .custom-phone-input {
    background-color: transparent !important;
    border: none !important;
    color: #fff !important;
    height: 50px !important;
    font-size: 16px !important;
    width: 100% !important;

    ::placeholder {
      color: #888 !important;
      opacity: 0.8 !important;
    }
  }

  .flag-dropdown {
    background-color: transparent !important;
    border: none !important;
    border-radius: 12px 0 0 12px !important;
  }

  .selected-flag {
    width: 60px !important;
    background-color: transparent !important;
    padding-left: 16px;
    &:hover {
      background-color: transparent !important;
    }
  }
`;

const Label = styled.label`
  position: absolute;
  top: -8px;
  left: 16px;
  font-size: 14px;
  font-weight: 300;
  color: #888;
  background: linear-gradient(
    0deg,
    rgba(23, 23, 23, 1) 0%,
    rgba(26, 26, 26, 1) 100%
  );
  z-index: 2;
`;

const Error = styled.span`
  font-size: 14px;
  line-height: 19px;
  color: #ff5151;
`;
